import { useMemo } from 'react';
import { useModal } from '@ebay/nice-modal-react';
import { ColumnDef } from '@tanstack/react-table';
import { Edit, Pause, PhoneOutgoing, Play, Plus } from 'lucide-react';
import Section from '@/components/Section';
import { Button } from '@/components/ui/button';
import useCampaigns from '@/hooks/useCampaigns';
import { ICampaign } from '@/types';
import DataTable from '../DataTable/DataTable';
import Loading from '../Loading';
import { Badge } from '../ui/badge';

const Campaigns = () => {
  const campaigns = useCampaigns();
  const campaignDrawer = useModal('campaign-drawer');
  const runCampaignModal = useModal('run-campaign-modal');

  const data = useMemo(() => campaigns.data || [], [campaigns.data]);

  const columns: ColumnDef<ICampaign>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => {
          return (
            <>
              <Button
                variant={'outline'}
                className="py-1.5 rounded-full px-2 text-sm cursor-pointer"
                onClick={() => {
                  campaignDrawer.show({ campaign: row.original });
                }}
              >
                <div className="flex items-center gap-1">
                  <PhoneOutgoing />
                  {row.original.name}
                </div>
              </Button>
            </>
          );
        },
      },
      {
        accessorKey: 'description',
        header: 'Description',
      },
      {
        accessorKey: 'pathway_id',
        header: 'Pathway',
      },
      {
        accessorKey: 'total_contacts',
        header: 'Contacts',
      },
      {
        accessorKey: 'total_calls',
        header: 'Calls',
        cell: ({ row }) => (
          <>
            {row.original.total_calls_completed}/{row.original.total_calls}
          </>
        ),
      },
      {
        accessorKey: 'total_batches',
        header: 'Batches',
        cell: ({ row }) => (
          <>
          {row.original.total_batches_completed}/{row.original.total_batches}
          </>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => <Badge>{row.original.status}</Badge>,
      },
      {
        header: 'Actions',
        action: true,
        cell: ({
          row,
        }: {
          row: {
            original: ICampaign;
          };
        }) => (
          <div className="flex items-center gap-2">
            <Button
              onClick={() => {
                campaignDrawer.show({ campaign: row.original });
              }}
            >
              <Edit />
              Edit
            </Button>
            {row.original.status === 'draft' && (
              <Button
                onClick={() => {
                  runCampaignModal.show({ campaignId: row.original.id, action: 'start' });
                }}
              >
                <Play />
                Start Campaign
              </Button>
            )}
            {row.original.status === 'running' && (
              <Button
                onClick={() => {
                  runCampaignModal.show({ campaignId: row.original.id, action: 'pause' });
                }}
                variant={'destructive'}
              >
                <Pause />
                Pause Campaign
              </Button>
            )}
            {row.original.status === 'pausing' && (
              <Button>
                <Loading />
                Pausing Campaign
              </Button>
            )}
            {row.original.status === 'paused' && (
              <Button
                onClick={() => {
                  runCampaignModal.show({ campaignId: row.original.id, action: 'resume' });
                }}
                className="text-white bg-green-500"
              >
                <Play />
                Resume Campaign
              </Button>
            )}
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <div className="flex items-center justify-between gap-2 mb-2">
        <h2 className="mb-4 text-lg font-bold">Campaign</h2>
        <Button className="ml-auto" onClick={() => campaignDrawer.show()}>
          <Plus />
          Add Campaign
        </Button>
      </div>

      <Section>
        <DataTable data={data} columns={columns} model={campaigns} placeholder={'Search Campaigns...'} />
      </Section>
    </>
  );
};

export default Campaigns;
